import {
  COLLAPSIBLE_HEADER_CONTENT_TOP_PADDING,
  CollapsibleScreenHeader,
} from "@/components/prooftree/CollapsibleScreenHeader"
import { EmptyState } from "@/components/prooftree/EmptyState"
import { GlowField } from "@/components/prooftree/GlowField"
import { PressableScale } from "@/components/prooftree/PressableScale"
import { Box } from "@/components/ui/box"
import { Button, ButtonText } from "@/components/ui/button"
import { HStack } from "@/components/ui/hstack"
import { Radio, RadioGroup, RadioIndicator, RadioLabel } from "@/components/ui/radio"
import { Text } from "@/components/ui/text"
import { Textarea, TextareaInput } from "@/components/ui/textarea"
import { VStack } from "@/components/ui/vstack"
import { screenCopy } from "@/constants/Brand"
import { Colors } from "@/constants/Colors"
import { useAuth } from "@/contexts/AuthContext"
import { allSkills } from "@/data/prooftree"
import { createEqJournalEntry } from "@/services/eqParityBackend"
import {
  EqEvidenceFile,
  pickProofEvidenceFile,
  validateEvidenceFile,
} from "@/services/proofEvidenceStorage"
import { getBackendModeLabel, submitEqProof } from "@/services/prooftreeBackend"
import { router } from "expo-router"
import { Award, Link, PlayCircle, Send, Sparkles, Upload, Video } from "lucide-react-native"
import { useRef, useState } from "react"
import { Animated, Platform, View } from "react-native"
import { SafeAreaView } from "react-native-safe-area-context"

export default function AddVideoProofTabScreen() {
  const auth = useAuth()
  const [level, setLevel] = useState("Level 3")
  const [videoSource, setVideoSource] = useState<"upload" | "link" | null>(null)
  const [evidenceFile, setEvidenceFile] = useState<EqEvidenceFile | null>(null)
  const [evidenceMessage, setEvidenceMessage] = useState("")
  const [reflection, setReflection] = useState("")
  const [submitMessage, setSubmitMessage] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const activeSkill = allSkills[0]
  const activeCheckpoint =
    activeSkill.levels.find((skillLevel) => skillLevel.title === level) ??
    activeSkill.levels.find((skillLevel) => skillLevel.status === "In Progress") ??
    activeSkill.levels[0]
  const scrollY = useRef(new Animated.Value(0)).current

  async function logProof() {
    if (!videoSource || isSubmitting) return
    if (videoSource === "upload" && !evidenceFile) {
      setEvidenceMessage("Choose an evidence file before logging this proof.")
      return
    }
    if (videoSource === "upload" && evidenceFile) {
      const validationError = validateEvidenceFile(evidenceFile)
      if (validationError) {
        setEvidenceMessage(validationError)
        return
      }
    }

    setIsSubmitting(true)
    const result = await submitEqProof({
      skillId: activeSkill.id,
      skillIds: [activeSkill.id],
      level,
      title: `${activeSkill.title} proof`,
      reflection,
      evidenceKind: videoSource,
      evidenceFile: videoSource === "upload" ? evidenceFile : null,
      proofType: videoSource === "upload" ? "mixed" : "link",
    })
    let nextMessage = result.message
    if (reflection.trim() && (!auth.session || result.persisted)) {
      const journalResult = await createEqJournalEntry({
        title: `${activeSkill.title} proof`,
        reflectionText: reflection,
        skillIds: [activeSkill.id],
        proofId: result.persisted ? result.id : null,
        visibility: "private",
      })
      nextMessage = `${result.message} ${journalResult.message}`
    }
    setSubmitMessage(nextMessage)
    setSubmitted(!auth.session || result.persisted)
    setIsSubmitting(false)
  }

  if (submitted) {
    return (
      <SafeAreaView
        className="flex-1"
        edges={["top"]}
        style={{ backgroundColor: Colors.dark.surface }}
      >
        <GlowField />
        <VStack className="flex-1 items-center justify-center px-6">
          <Box
            className="mb-6 h-28 w-28 items-center justify-center rounded-full border"
            style={{
              backgroundColor: `${Colors.dark.proof}2E`,
              borderColor: `${Colors.dark.proof}B8`,
              shadowColor: Colors.dark.proof,
              shadowOpacity: 0.32,
              shadowRadius: 24,
            }}
          >
            <Award color={Colors.dark.proof} size={56} />
          </Box>
          <Text className="text-center text-3xl font-bold text-white">Great work, coach!</Text>
          <Text className="mt-3 text-center leading-6" style={{ color: Colors.dark.textSecondary }}>
            {submitMessage ||
              "Proof captured. Supabase or local fallback status appears in the save message."}
          </Text>
          <Button
            size="xl"
            className="mt-8 w-full rounded-2xl"
            style={{ backgroundColor: Colors.dark.proof }}
            onPress={() => {
              setSubmitted(false)
              setVideoSource(null)
              setEvidenceFile(null)
              setEvidenceMessage("")
              setReflection("")
              setSubmitMessage("")
            }}
          >
            <ButtonText className="font-bold" style={{ color: Colors.dark.primaryForeground }}>
              Log Another Proof
            </ButtonText>
          </Button>
        </VStack>
      </SafeAreaView>
    )
  }

  return (
    <SafeAreaView
      className="flex-1"
      edges={["top"]}
      style={{ backgroundColor: Colors.dark.surface }}
    >
      <GlowField />
      <CollapsibleScreenHeader
        scrollY={scrollY}
        eyebrow={`${activeCheckpoint.title} • ${activeCheckpoint.status}`}
        title={`Coaching ${activeSkill.title}`}
        subtitle={screenCopy.proof.subtitle}
        accentColor={Colors.dark.proof}
        statusText={getBackendModeLabel()}
      />
      <Animated.ScrollView
        className="flex-1"
        contentContainerStyle={{
          padding: 20,
          paddingTop: COLLAPSIBLE_HEADER_CONTENT_TOP_PADDING,
          paddingBottom: Platform.OS === "web" ? 118 : 102,
        }}
        showsVerticalScrollIndicator={false}
        scrollEventThrottle={16}
        onScroll={Animated.event([{ nativeEvent: { contentOffset: { y: scrollY } } }], {
          useNativeDriver: false,
        })}
      >
        <Button
          size="xl"
          className="mb-5 rounded-2xl"
          style={{
            backgroundColor: Colors.dark.primary,
            shadowColor: Colors.dark.primary,
            shadowOpacity: 0.24,
            shadowRadius: 18,
            shadowOffset: { width: 0, height: 10 },
          }}
          onPress={() =>
            router.push({
              pathname: "/coach/[skillId]",
              params: { skillId: activeSkill.id },
            })
          }
        >
          <Sparkles color={Colors.dark.primaryForeground} size={18} />
          <ButtonText className="font-bold" style={{ color: Colors.dark.primaryForeground }}>
            Coach this moment
          </ButtonText>
        </Button>

        <Box
          className="mb-5 h-60 overflow-hidden rounded-3xl border"
          style={{
            backgroundColor: Colors.dark.surfaceSecondary,
            borderColor: videoSource ? `${Colors.dark.proof}B8` : `${Colors.dark.proof}38`,
          }}
        >
          <View
            className="absolute inset-0"
            style={{
              backgroundColor: videoSource ? `${Colors.dark.proof}1A` : "rgba(255,255,255,0.02)",
            }}
          />
          <VStack className="flex-1 items-center justify-center px-5">
            <Box
              className="mb-4 h-20 w-20 items-center justify-center rounded-full border"
              style={{
                backgroundColor: `${Colors.dark.proof}24`,
                borderColor: videoSource ? `${Colors.dark.proof}B8` : "rgba(255,255,255,0.08)",
              }}
            >
              {videoSource ? (
                <PlayCircle color={Colors.dark.proof} size={38} />
              ) : (
                <Video color={Colors.dark.proof} size={36} />
              )}
            </Box>
            <Text className="font-bold text-white">
              {videoSource ? "Proof ready to log" : "Proof preview appears here"}
            </Text>
            <Text className="mt-1 text-center text-sm" style={{ color: Colors.dark.textSecondary }}>
              {videoSource === "upload"
                ? evidenceFile
                  ? `${evidenceFile.name} selected. Add a reflection when the moment is fresh.`
                  : "Choose a video, image, PDF, or text evidence file."
                : videoSource === "link"
                  ? "Link evidence selected. Your coach can help unpack what happened."
                  : "Add a short video, TikTok, or YouTube link"}
            </Text>
          </VStack>
        </Box>

        {!videoSource ? (
          <Box className="mb-5">
            <EmptyState
              icon={Video}
              title="No proof selected yet."
              description="This is where real growth begins. Choose a file or link to coach this moment."
            />
          </Box>
        ) : null}

        <HStack className="mb-6 gap-3">
          <PressableScale
            className="flex-1"
            accessibilityRole="button"
            testID="proof-upload-source"
            onPress={async () => {
              setVideoSource("upload")
              setEvidenceMessage("")
              const pickedFile = await pickProofEvidenceFile()
              if (!pickedFile) return
              setEvidenceFile(pickedFile)
              const validationError = validateEvidenceFile(pickedFile)
              setEvidenceMessage(validationError ?? "Evidence file ready to upload.")
            }}
          >
            <Box
              className="min-h-28 rounded-2xl border p-4"
              style={{
                backgroundColor:
                  videoSource === "upload"
                    ? `${Colors.dark.proof}29`
                    : Colors.dark.surfaceSecondary,
                borderColor:
                  videoSource === "upload" ? Colors.dark.proof : `${Colors.dark.proof}33`,
              }}
            >
              <HStack className="items-center gap-3">
                <Box
                  className="h-10 w-10 items-center justify-center rounded-full"
                  style={{ backgroundColor: `${Colors.dark.proof}24` }}
                >
                  <Upload color={Colors.dark.proof} size={20} />
                </Box>
                <VStack className="flex-1">
                  <Text className="font-bold text-white">Upload video</Text>
                  <Text
                    className="mt-1 text-xs leading-4"
                    style={{ color: Colors.dark.textSecondary }}
                  >
                    {evidenceFile?.name ?? "Select a file"}
                  </Text>
                </VStack>
              </HStack>
            </Box>
          </PressableScale>
          <PressableScale
            className="flex-1"
            accessibilityRole="button"
            testID="proof-link-source"
            onPress={() => {
              setVideoSource("link")
              setEvidenceFile(null)
              setEvidenceMessage("")
            }}
          >
            <Box
              className="min-h-28 rounded-2xl border p-4"
              style={{
                backgroundColor:
                  videoSource === "link" ? `${Colors.dark.proof}29` : Colors.dark.surfaceSecondary,
                borderColor: videoSource === "link" ? Colors.dark.proof : `${Colors.dark.proof}33`,
              }}
            >
              <HStack className="items-center gap-3">
                <Box
                  className="h-10 w-10 items-center justify-center rounded-full"
                  style={{ backgroundColor: `${Colors.dark.proof}24` }}
                >
                  <Link color={Colors.dark.proof} size={20} />
                </Box>
                <VStack className="flex-1">
                  <Text className="font-bold text-white">Paste link</Text>
                  <Text
                    className="mt-1 text-xs leading-4"
                    style={{ color: Colors.dark.textSecondary }}
                  >
                    TikTok or YouTube
                  </Text>
                </VStack>
              </HStack>
            </Box>
          </PressableScale>
        </HStack>

        {evidenceMessage ? (
          <Box
            className="mb-5 rounded-2xl border p-4"
            style={{
              backgroundColor: `${Colors.dark.proof}14`,
              borderColor: `${Colors.dark.proof}38`,
            }}
          >
            <Text className="text-sm leading-5" style={{ color: Colors.dark.textPrimary }}>
              {evidenceMessage}
            </Text>
          </Box>
        ) : null}

        <Box
          className="mb-4 rounded-2xl border p-4"
          style={{
            backgroundColor: `${Colors.dark.proof}14`,
            borderColor: `${Colors.dark.proof}38`,
          }}
        >
          <HStack className="items-start gap-3">
            <Sparkles color={Colors.dark.proof} size={18} />
            <Text className="flex-1 text-sm leading-5" style={{ color: Colors.dark.textPrimary }}>
              EMO.coach suggests: {screenCopy.proof.reflectionPrompt}
            </Text>
          </HStack>
        </Box>

        <Text className="mb-2 font-bold text-white">Reflection</Text>
        <Textarea
          size="lg"
          className="mb-6 h-44 rounded-2xl border"
          style={{
            backgroundColor: Colors.dark.surfaceSecondary,
            borderColor: `${Colors.dark.proof}33`,
          }}
        >
          <TextareaInput
            className="p-4 text-white"
            placeholder={screenCopy.proof.reflectionPrompt}
            placeholderTextColor={Colors.dark.textSecondary}
            value={reflection}
            onChangeText={setReflection}
            multiline
          />
        </Textarea>

        <Text className="mb-3 font-bold text-white">Checkpoint level</Text>
        <RadioGroup value={level} onChange={setLevel} className="mb-7 gap-3">
          {["Level 1", "Level 2", "Level 3", "Level 4"].map((item) => (
            <Radio key={item} value={item}>
              <Box
                className="flex-1 rounded-2xl border p-4"
                style={{
                  backgroundColor:
                    level === item ? `${Colors.dark.proof}24` : Colors.dark.surfaceSecondary,
                  borderColor: level === item ? Colors.dark.proof : "rgba(255,255,255,0.07)",
                }}
              >
                <HStack className="items-center gap-3">
                  <RadioIndicator />
                  <RadioLabel className="font-bold text-white">{item}</RadioLabel>
                </HStack>
              </Box>
            </Radio>
          ))}
        </RadioGroup>

        <Button
          size="xl"
          className="rounded-2xl border"
          disabled={!videoSource || isSubmitting}
          style={{
            backgroundColor: "transparent",
            borderColor: `${Colors.dark.proof}88`,
            opacity: videoSource && !isSubmitting ? 1 : 0.54,
          }}
          onPress={logProof}
        >
          <Send color={Colors.dark.proof} size={18} />
          <ButtonText className="font-bold" style={{ color: Colors.dark.proof }}>
            {isSubmitting ? "Logging..." : "Log Proof"}
          </ButtonText>
        </Button>
      </Animated.ScrollView>
    </SafeAreaView>
  )
}
